import { useState, useEffect } from 'react'
import { SidebarButton, LoadingBar, SidebarSpacing, Scrollable } from '@/components/DashboardComponents'
import { Outlet, useRouteError, useNavigate, useLoaderData, useLocation } from 'react-router'
import { Icon } from '@/components/Icon'
import { Button } from '@/form/FormComponents'
import { Header, CircularMenuButton, Sidebar } from '@/components/DashboardComponents'
import { signOut, useUser, useIsDeveloper, useSetPrivacyMode } from '@/systems/Authentication'
import { useListThenJoinDepartmentModal } from '@/pages/SettingsLayout'
import { useModal, Modal, ModalContent, ModalButtons, useHelpModal } from '@/components/Modal'
import { Checkbox } from '@/form/FormComponents';
import { useIsMobile } from '@/hooks/useIsMobile'
import { usePreference } from '@/hooks/useStorage';
import { INTERNAL_USERS } from '@/pages/internal/InternalLayout';

export function DashboardLayout() {
  const navigate = useNavigate();
  const { userdata } = useUser();
  const { value: courses, has_new_shared_files_as_of_date } = useLoaderData().data;
  const [date_last_checked_shared_with_me] = usePreference("date_last_checked_shared_with_me", 0);
  const show_new_shared_files_dot = has_new_shared_files_as_of_date && has_new_shared_files_as_of_date > date_last_checked_shared_with_me;
  const location = useLocation();
  const is_fullscreen = 
    location.pathname.match(/^\/settings\/?/) || 
    location.pathname.match(/^\/internal\/?/) || 
    location.pathname.match(/^(\/organizations\/[A-Za-z0-9]+)?\/courses\/[A-Za-z0-9]+\/assessments\/[A-Za-z0-9]+\/.+?$/) ||
    location.pathname.match(/^\/scenarios\/.+?\/(edit|preview)+?$/) ||
    location.pathname.match(/^\/rubrics\/.+?\/(edit)+?$/) ||
    location.pathname.match(/^\/session\/.+?$/);
  const manager_organizations = userdata.organizations.filter(o => o.roles.includes("admin"));
  const is_mobile = useIsMobile();
  const [sidebar_open, setSidebarOpen] = useState(false);

  const [loading_join_department, setLoadingJoinDepartment] = useState(false);

  const { handleJoinDepartment } = useListThenJoinDepartmentModal({ setLoadingJoinDepartment });

  const [openContactUsModal] = useModal(function({ closeModal }) {
    return (
      <Modal title="Talk to sales">
        <ModalContent>
          Please email us at <a href="mailto:contact@sapientclinician.com" className="links">contact@sapientclinician.com</a> to talk about bringing Sapient Clinician to your team, department, or organization.
        </ModalContent>
        <ModalButtons>
          <Button text="Dismiss" onClick={closeModal}/>
        </ModalButtons>
      </Modal>
    )
  })

  if (is_fullscreen) {
    return <Outlet/>;
  }

  const course_id = location.pathname.match(/^\/courses\/([A-Za-z0-9]+)\/?/)?.[1];

  const sidebar_courses = courses.filter(c => !c.archived).sort((a, b) => a.date_created - b.date_created).slice(0, 8).sort((a, b) => a.name.localeCompare(b.name));

  const banner_chip = ((userdata.is_educator && userdata.organizations.length == 0) ? (
    userdata.organization_id ? (
      userdata.organizations_pending.length > 0 ? (
        <div className="banner_chip">
          <div>
            <div><Icon name="domain"/></div>
            <div>{`Department join request is pending`}</div>
          </div>
        </div>
      ) : (
        <div className="banner_chip">
          <div>
            <div></div>
            <div></div>
            <div>
              <Button text="Join a department" className="hollow small" onClick={handleJoinDepartment} loading={loading_join_department}/>
            </div>
          </div>
        </div>
      )
    ) : (
      // <div className="callout_hint_box">
      //   <div>
      //     <div><Icon name="domain"/></div>
      //     {/* <div>{`Your account is limited without educator access. Sign up your organization to gain educator access.`}</div> */}
      //     <div>{`You have a free trial account. Sign up as an individual, team, or department for full access.`}</div>
      //   </div>
      // </div>

      // <div className="banner_chip">
      //   <div>
      //     <div><Icon name="gift-outline"/></div>
      //     <div>{`Early access`}</div>
      //     <div>
      //       <Button text="Talk to sales" className="small hollow" onClick={openContactUsModal}/>
      //     </div>
      //   </div>
      // </div>

      null
    )
  ) : null)

  return (
    <div className="dashboard_container">
      <header className="dashboard_topbar">
        <div className="topbar_left">
          {is_mobile && <CircularMenuButton onClick={() => {setSidebarOpen(true)}} icon="menu"/>}
          <a className="logo" onClick={() => navigate("/")}>
            <Icon name="Lockup"/>
          </a>
        </div>
        <div className="topbar_right">
          {banner_chip}
        </div>
        <div className="topbar_right">
          <AccountMenu/>
        </div>
      </header>
      <div className="dashboard_main">
        <Sidebar type="main" sidebar_open={sidebar_open} setSidebarOpen={setSidebarOpen}>
          {
            userdata.is_educator ? (
              <>
                <SidebarButton to="/scenarios" icon="forum-outline" name="My scenarios" end/>
                <SidebarButton to="/rubrics" icon="bullseye-arrow" name="My rubrics" end/>
                {/* <SidebarSpacing/> */}
                <SidebarButton to="/shared" icon="account-multiple-outline" name="Shared with me" show_dot={show_new_shared_files_dot}/>
                <SidebarButton to="/trash" icon="trash-can-outline" name="Trash"/>
                <SidebarSpacing/>
              </>
            ) : null
          }
          <SidebarButton to="/courses" icon="book-open-variant-outline" name="Courses" end/>
          {sidebar_courses.map((course) => <SidebarButton key={course._id} to={`/courses/${course._id}`} name={course.name+(course.section_name ? ` (${course.section_name})` : "")} icon="empty"/>)}
          {courses.filter(c => c.archived).length > 0 ? <SidebarButton to="/archived-courses" icon="archive" name="Archived courses" selected={courses.find(c => c._id == course_id && c.archived) ? true : "default"}/> : null}
          {manager_organizations?.length > 0 ? <>
            <SidebarSpacing/>
            {
              manager_organizations.every(o => !o.data.parent) ? (
                <SidebarButton to="/organizations" icon="domain" name={"Organization"+((manager_organizations.length > 1) ? "s" : "")}/>
              ) : (
                <SidebarButton to="/organizations" icon="domain" name={"Department"+((manager_organizations.length > 1) ? "s" : "")}/>
              )
            }
            {/* {manager_organizations.map((organization) => <SidebarButton key={organization.data._id} to={`/organizations/${organization.data._id}`} name={organization.data.shortname}/>)} */}
          </> : null}
        </Sidebar>
        <Scrollable className="dashboard_content" remember={true}>
          <Outlet/>
        </Scrollable>
      </div>
    </div>
  )
}

export function AccountMenu() {
  const navigate = useNavigate();
  const { user, userdata, uid } = useUser();
  const isDeveloper = useIsDeveloper();
  const [open, setOpen] = useState(false);
  const [openHelpModal] = useHelpModal();
  const { privacyMode, setPrivacyMode } = useSetPrivacyMode();

  useEffect(() => {
    if (!user.email && userdata.name == "Demo User") {
      signOut(true);
    }
  }, [user.email, userdata.name]);

  function openMenu() {
    setOpen(true);
  }

  function closeMenu() {
    setOpen(false);
  }

  function handleSignOut() {
    signOut(true);
  }

  return (
    <>
      <CircularMenuButton onClick={() => {open ? closeMenu() : openMenu()}} icon="account-circle-outline"/>
      {open ? <>
        <div className="account_menu_background" onClick={closeMenu}></div>
        <div className="account_menu">
          <div className="account_menu_top">
            <div>{userdata.name || "You"}</div>
            <div style={!user.email ? {display: "none"} : {}}>{user.email}</div>
            {userdata.organization_data ? <div>Managed by {userdata.organization_data.shortname}</div> : null}
          </div>
          {(userdata.email?.endsWith("@sapientclinician.com") && userdata.email !== "demo@sapientclinician.com") ? (
            <div style={{display: "flex", paddingLeft: "19px"}}>
              <Checkbox text="Hide learner names" value={privacyMode} onChange={setPrivacyMode}/>
            </div>
          ) : null}
          <div className="account_menu_bottom">
            <ul>
              {(user.isAnonymous || userdata.email === "demo@sapientclinician.com") ? null : <li>
                <a className="account_menu_option" onClick={() => navigate("/settings")} tabIndex={0}>
                  <Icon name="account-cog-outline"/>
                  <span>Account settings</span>
                </a>
              </li>}
              {(INTERNAL_USERS.includes(uid) && <li>
                <a className="account_menu_option" onClick={() => window.open("/internal/activity-graph", "_blank")} tabIndex={0}>
                  <Icon name="tools"/>
                  <span>Internal admin tools</span>
                </a>
              </li>)}
              {user.isAnonymous ? null : <li>
                <a className="account_menu_option" onClick={() => {openHelpModal(); closeMenu();}} tabIndex={0}>
                  <Icon name="lifebuoy"/>
                  <span>Help and support</span>
                </a>
              </li>}
              <li>
                <a className="account_menu_option" onClick={handleSignOut} tabIndex={0}>
                  <Icon name="logout"/>
                  <span>Sign out</span>
                </a>
              </li>
            </ul>
          </div> 
        </div>
      </> : null}
    </>
  )
}

export function DashboardLayoutErrorBoundary({ title, text, path }) {
  return (
    <>
      <Header title={title} backButton={text ? (path ? path : "/") : undefined}/>
      <div className="dashboard_content_inner nearest_scrollable_container">
        <GenericErrorBoundary text={text} path={path}/>
      </div>
    </>
  )
}

export function FullpageGenericErrorBoundary() {
  return (
    <>
      <LoadingBar/><GenericErrorBoundary/>
    </>
  )
}

export function GenericErrorBoundary({ text, path }) {
  let navigate = useNavigate();
  let error = useRouteError();
  console.error(error);

  if (error.status || text) {
    return (
      <div className="error_box">
        <div>
          <div><Icon name="alert-circle-outline"/></div>
          <div>{text ? text : (error.status == 404 ? (error.text || "Page not found") : (error.text || "Something went wrong"))}</div>
          <div>
            <Button text="Go back" onClick={() => navigate(path ? path : (error.path || "/"))}/>
          </div>
        </div>
      </div>
    )
  } else {
    return (
      <div className="error_box">
        <div>
          <div><Icon name="alert-circle-outline"/></div>
          <div>Something went wrong</div>
          <div>
            <Button text="Try again" className="hollow" onClick={() => window.location.reload()}/>
          </div>
        </div>
      </div>
    )
  }
}

export function GenericEmptyListPlaceholder({icon, text, inline = false, error = false, handleClick}) {
  if (error) {
    icon = "alert-circle-outline";
    text = "Something went wrong";
  }

  return (
    <div className={"error_box" + (inline ? " error_box_inline" : "")}>
      <div>
        <div style={{display: (icon ? "" : "none")}}><Icon name={icon}/></div>
        <div>{text}</div>
        {error && <div>
          <Button text="Try again" className="hollow" onClick={handleClick}/>
        </div>}
      </div>
    </div>
  )
}

export function SplashScreen() {
  return (
    <div className="splash_screen">
      <div>
        <div>
          <Icon name="sapient logo"/>
        </div>
        <div className="indeterminate"><div></div></div>
        <div style={{height: "0px", overflow: "hidden", fontFamily: "var(--fonts)", fontWeight: "400"}}>Loading...</div>
        <div style={{height: "0px", overflow: "hidden", fontFamily: "var(--fonts)", fontWeight: "500"}}>Loading...</div>
        <div style={{height: "0px", overflow: "hidden", fontFamily: "var(--fonts)", fontWeight: "700"}}>Loading...</div>
        <div style={{height: "0px", overflow: "hidden", fontFamily: "var(--fonts)", fontWeight: "400", fontStyle:"italic"}}>Loading...</div>
        <div style={{height: "0px", overflow: "hidden", fontFamily: "var(--fonts)", fontWeight: "500", fontStyle:"italic"}}>Loading...</div>
        <div style={{height: "0px", overflow: "hidden", fontFamily: "var(--fonts)", fontWeight: "700", fontStyle:"italic"}}>Loading...</div>
      </div>
    </div>
  )
}